export function formatPLN(v: number): string {
  return v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Headline variant of `formatPLN` for stat-tile values: drops the cents once
 * the figure is big enough that they are noise (>= 1 000 PLN), keeps them
 * below that. A KPI tile is a headline number, and "3 550 000,00" simply does
 * not fit a half-width tile on a 390px screen — it got clipped to
 * "3 550 000…", which is worse than no decimals. The exact value stays
 * reachable via the tile's title attribute and the charts below.
 *
 * Not an ad-hoc formatter: every PLN figure in the app goes through this file.
 */
export function formatPLNCompact(v: number): string {
  return Math.abs(v) >= 1000
    ? v.toLocaleString('pl-PL', { maximumFractionDigits: 0 })
    : formatPLN(v);
}

const moneyFormatters = new Map<number, Intl.NumberFormat>();

function moneyFormat(maximumFractionDigits: number): Intl.NumberFormat {
  let nf = moneyFormatters.get(maximumFractionDigits);
  if (!nf) {
    // pl-PL groups with a narrow no-break space (U+202F) — keep it, that is
    // the Polish convention and it never wraps mid-number.
    nf = new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: maximumFractionDigits === 0 ? 0 : 2,
      maximumFractionDigits,
    });
    moneyFormatters.set(maximumFractionDigits, nf);
  }
  return nf;
}

/**
 * `142 500 zł` / `12 000 EUR`; `fractionDigits` 2 for detail views. The one
 * money formatter for any currency in the app — PLN gets the "zł" suffix,
 * everything else the ISO code.
 */
export function formatMoney(value: number, currency: string, fractionDigits = 0): string {
  const suffix = currency === 'PLN' ? 'zł' : currency;
  return `${moneyFormat(fractionDigits).format(value)} ${suffix}`;
}

/** Parses "45 000,50" / "45000.5" → 45000.5; null for empty, NaN-guarded. */
export function parseDecimal(text: string): number | null {
  // \u00A0 / \u202F: the NBSP and narrow NBSP that pl-PL formatting (and paste) emit as group separators.
  const cleaned = text.replace(/\s|\u00A0|\u202F/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}
